import { Search as SearchIcon } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDeliveriesMutation } from 'api/hooks/useDeliveriesMutation';
import { useSuppliers } from 'api/hooks/useSuppliers';
import { DeliveriesSortField, DeliveryStatus, HistoricalSwitch } from 'api/models/Deliveries';
import { SortOrder } from 'api/models/Sorting';
import theme from 'App.theme';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import { useLanguage } from 'context/AppContext';
import useSupplierLabels from 'hooks/useSupplierLabels';
import { ComponentProps, useCallback, useEffect, useMemo } from 'react';
import { formatDateToYYYYMMDD } from 'utils/helpers/DateTimeFormat.helpers';
import { useTurnaroundSupplier } from '../hooks/useTurnaroundSupplier';
import { TurnaroundSurgeriesList } from './TurnaroundSurgeriesList';
import { TurnaroundPageVariant } from '../models';
import { TurnaroundCard } from './TurnaroundCard';
import { useTurnaroundDestinationDeliveryId } from '../hooks/useTurnaroundDestinationDeliveryId';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';
import { useTurnaroundOriginForm } from '../hooks/useTurnaroundOriginForm';
import { useTurnaroundDestinationForm } from '../hooks/useTurnaroundDestinationForm';
import { useHasSearchedForDestinationDeliveries } from '../hooks/useHasSearchedForDestinationDeliveries';
import useSurgeonSelectOptions from 'hooks/useSurgeonSelectOptions';

const TURNAROUND_ORIGIN_DELIVERY_STATUSES: DeliveryStatus[] = [
  'CHECKED_IN',
  'PARTIALLY_CHECKED_OUT',
  'CHECKED_OUT',
  'CANCELED',
];
const TURNAROUND_DESTINATION_DELIVERY_STATUSES: DeliveryStatus[] = ['RECEIVED', 'PARTIALLY_CHECKED_IN', 'CHECKED_IN'];

type Props = {
  variant: TurnaroundPageVariant;
} & ComponentProps<'div'>;

export const TurnaroundSearchSurgeries = ({ variant, ...props }: Props) => {
  const lang = useLanguage()[0].TurnaroundSearchSurgeries;

  // Suppliers
  const { data: suppliers, isLoading: suppliersLoading } = useSuppliers();
  const supplierLabels = useSupplierLabels({ suppliers: suppliers?.suppliers });
  const supplierOptions = useMemo(
    () =>
      (suppliers?.suppliers || []).map(supplier => ({
        id: supplier.tenantId,
        label: supplierLabels[supplier.name],
        name: supplier.name,
      })),
    [supplierLabels, suppliers?.suppliers],
  );

  // Surgeons
  const { surgeonOptions, surgeonsLoading } = useSurgeonSelectOptions({ removeDuplicates: true });

  // Current User
  const { data: currentUser, isLoading: currentUserLoading } = useCurrentUser();

  // State
  const [supplier, setSupplier] = useTurnaroundSupplier();
  const [turnaroundOriginForm, setTurnaroundOriginForm] = useTurnaroundOriginForm();
  const [turnaroundDestinationForm, setTurnaroundDestinationForm] = useTurnaroundDestinationForm();
  const turnaroundForm = variant === 'origin' ? turnaroundOriginForm : turnaroundDestinationForm;
  const setTurnaroundForm = variant === 'origin' ? setTurnaroundOriginForm : setTurnaroundDestinationForm;
  const setTurnaroundOriginDeliveryId = useTurnaroundOriginDeliveryId()[1];
  const setTurnaroundDestinationDeliveryId = useTurnaroundDestinationDeliveryId()[1];
  const setHasSearchedForDestinationDeliveries = useHasSearchedForDestinationDeliveries()[1];

  // Deliveries
  const {
    mutate: fetchDeliveries,
    reset: resetDeliveries,
    data: deliveries,
    isLoading: deliveriesLoading,
    isSuccess: deliveriesSuccess,
  } = useDeliveriesMutation();

  const searchDisabled =
    !supplier || !turnaroundForm.surgeryDate || (variant === 'destination' && !turnaroundForm.surgeon);

  // Filter the deliveries based on the variant
  // BE doesn't support multi-status querying so we need to filter on the FE
  const filteredDeliveries = useMemo(() => {
    // Use a Set to avoid duplicates
    const allDeliveries = deliveries?.result || [];

    return allDeliveries
      .filter(delivery =>
        variant === 'origin'
          ? TURNAROUND_ORIGIN_DELIVERY_STATUSES.includes(delivery.status)
          : TURNAROUND_DESTINATION_DELIVERY_STATUSES.includes(delivery.status),
      )
      .slice(0, 25); // limit to 25 for performance
  }, [deliveries?.result, variant]);

  // If the supplier changes and this is the destination variant, reset the deliveries
  useEffect(() => {
    if (variant === 'destination') {
      resetDeliveries();
    }
  }, [resetDeliveries, supplier, variant]);

  // If the search fields are empty, reset the deliveries
  useEffect(() => {
    if (
      !supplier &&
      !turnaroundForm.orderNumber &&
      !turnaroundForm.surgeryReference &&
      !turnaroundForm.deliveryNumber &&
      !turnaroundForm.surgeon &&
      !turnaroundForm.surgeryDate
    ) {
      resetDeliveries();
    }
  }, [
    resetDeliveries,
    supplier,
    turnaroundForm.orderNumber,
    turnaroundForm.surgeryReference,
    turnaroundForm.deliveryNumber,
    turnaroundForm.surgeon,
    turnaroundForm.surgeryDate,
  ]);

  // If the user leaves the page, reset hasSearchedForDestinationDeliveries
  useEffect(() => {
    return () => {
      setHasSearchedForDestinationDeliveries(false);
    };
  }, [setHasSearchedForDestinationDeliveries]);

  const onSearchDeliveries = useCallback(async () => {
    const params: Parameters<typeof fetchDeliveries>[0] = {
      sort_by: DeliveriesSortField.SurgeryDate,
      sort_order: SortOrder.Descending,
    };

    if (supplier) {
      params.supplier = {
        value: supplier.name,
        label: '',
      };
    }

    if (turnaroundForm.surgeon) {
      params.surgeon = turnaroundForm.surgeon.name;
    }

    if (turnaroundForm.orderNumber) {
      params.search_order_number = turnaroundForm.orderNumber;
    }

    if (turnaroundForm.deliveryNumber) {
      params.search_delivery_number = turnaroundForm.deliveryNumber;
    }

    let formattedSurgeryDate = '';

    if (turnaroundForm.surgeryDate) {
      formattedSurgeryDate = formatDateToYYYYMMDD(turnaroundForm.surgeryDate);
      params.surgery_date = formattedSurgeryDate;
    }

    if (turnaroundForm.surgeryReference) {
      params.search_surgery_reference = turnaroundForm.surgeryReference;
    }

    fetchDeliveries({ ...params, historical: HistoricalSwitch.All });

    // Set the flag to indicate that we have searched for destination deliveries
    if (variant === 'destination') {
      setHasSearchedForDestinationDeliveries(true);
    }

    // Reset the selected delivery ids
    if (variant === 'origin') {
      setTurnaroundOriginDeliveryId(null);
    } else {
      setTurnaroundDestinationDeliveryId(null);
    }

    // Analytics
    if (!currentUser) {
      throw new Error('Current user is not defined');
    }

    await trackEvent(
      variant === 'origin' ? EventType.TurnaroundSearchSurgeryOrigin : EventType.TurnaroundSearchSurgeryDestination,
      {
        current_user: currentUser.userId,
        tenant_name: currentUser.tenantName,
        supplier: supplier?.name || '',
        order_number: turnaroundForm.orderNumber || '',
        surgery_reference: turnaroundForm.surgeryReference || '',
        delivery_number: turnaroundForm.deliveryNumber || '',
        surgeon: turnaroundForm.surgeon?.label || '',
        surgery_date: formattedSurgeryDate,
      },
    );
  }, [
    currentUser,
    fetchDeliveries,
    setHasSearchedForDestinationDeliveries,
    setTurnaroundDestinationDeliveryId,
    setTurnaroundOriginDeliveryId,
    supplier,
    turnaroundForm.deliveryNumber,
    turnaroundForm.orderNumber,
    turnaroundForm.surgeon,
    turnaroundForm.surgeryDate,
    turnaroundForm.surgeryReference,
    variant,
  ]);

  return (
    <TurnaroundCard
      variant={variant}
      title={variant === 'origin' ? lang.title.origin : lang.title.destination}
      {...props}
    >
      <div
        style={{
          border: `1px solid ${theme.greys.GREYA2}`,
          borderTop: 0,
          backgroundColor: theme.colors.white,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.spacing.small,
          padding: theme.layout.spacing.small,
          paddingTop: theme.layout.spacing.medium,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: theme.layout.spacing.small,
          }}
        >
          <Autocomplete
            value={supplier}
            options={supplierOptions}
            disabled={
              suppliersLoading ||
              currentUserLoading ||
              // The supplier should only be changeable via the origin variant
              variant === 'destination'
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fullWidth
            size="small"
            renderInput={params => <TextField {...params} name="supplier" label={lang.formLabels.supplier} required />}
            onChange={(_, value) => {
              setSupplier(value);
            }}
          />

          <TextField
            name="orderNumber"
            label={lang.formLabels.orderNumber}
            value={turnaroundForm.orderNumber}
            fullWidth
            size="small"
            onChange={event => setTurnaroundForm({ ...turnaroundForm, orderNumber: event.target.value })}
          />
        </div>

        <div
          style={{
            display: 'flex',
            gap: theme.layout.spacing.small,
          }}
        >
          <TextField
            name="surgeryId"
            label={lang.formLabels.surgeryReference}
            value={turnaroundForm.surgeryReference}
            fullWidth
            size="small"
            onChange={event => setTurnaroundForm({ ...turnaroundForm, surgeryReference: event.target.value })}
          />

          <TextField
            name="deliveryNumber"
            label={lang.formLabels.deliveryNumber}
            value={turnaroundForm.deliveryNumber}
            fullWidth
            size="small"
            onChange={event => setTurnaroundForm({ ...turnaroundForm, deliveryNumber: event.target.value })}
          />
        </div>

        <div
          style={{
            display: 'flex',
            gap: theme.layout.spacing.small,
          }}
        >
          <Autocomplete
            value={turnaroundForm.surgeon}
            options={surgeonOptions}
            disabled={surgeonsLoading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fullWidth
            size="small"
            renderInput={params => (
              <TextField
                {...params}
                name="surgeon"
                label={lang.formLabels.surgeon}
                required={variant === 'destination'}
              />
            )}
            onChange={(_, value) => {
              setTurnaroundForm({ ...turnaroundForm, surgeon: value });
            }}
          />

          <div style={{ width: '100%' }}>
            <DatePicker
              // DatePicker doesn't re-render when the surgeryDate is cleared 🙄
              key={turnaroundForm.surgeryDate?.toString()}
              label={lang.formLabels.surgeryDate}
              selected={turnaroundForm.surgeryDate}
              required
              modalised
              size="small"
              onSelect={date => setTurnaroundForm({ ...turnaroundForm, surgeryDate: date })}
            />
          </div>
        </div>

        <Button
          startIcon={<SearchIcon />}
          colors={variant === 'origin' ? 'white on primary' : 'white on amber'}
          fullWidth
          disabled={searchDisabled || deliveriesLoading}
          loading={deliveriesLoading}
          onClick={onSearchDeliveries}
        >
          {lang.search}
        </Button>
      </div>

      <TurnaroundSurgeriesList variant={variant} deliveries={filteredDeliveries || []} hasSuccess={deliveriesSuccess} />
    </TurnaroundCard>
  );
};
